import { useEffect, useState, useCallback, useMemo } from 'react';
import { createRoute, redirect, useNavigate, useSearch } from '@tanstack/react-router';
import { useAuth0 } from '@auth0/auth0-react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  flexRender,
  createColumnHelper,
} from '@tanstack/react-table';
import { format } from 'date-fns';
import { Route as rootRoute } from '../__root';
import { Layout } from '@/client/layouts/layout';
import { usePersistedSearch } from '@/client/hooks/usePersistedSearch';
import { ResetFiltersButton } from '@/client/components/reset-filters-button';
import { ClientSelector } from '@/client/components/client-selector';
import { CacheTimestamp } from '@/client/components/cache-timestamp';
import { ClientNameCell } from '@/client/components/tables/cells';
import { API_BASE_URL } from '@/client/config';
import { cn } from '@/client/lib/utils';
import type {
  WebhookLogRow,
  WebhookLogsResponse,
  WebhookLogDetail,
  WebhookLogDetailResponse,
  WebhookLogStatus,
  WebhookLogRangeLabel,
} from '@shared/types/webhookLogs';
import { WEBHOOK_LOGS_CONFIG, WEBHOOK_LOG_RANGES } from '@shared/types/webhookLogs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/client/components/ui/table';
import { Button } from '@/client/components/ui/button';
import { Badge } from '@/client/components/ui/badge';
import { Input } from '@/client/components/ui/input';
import { JsonSyntaxHighlight } from '@/client/components/ui/json-syntax-highlight';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/client/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/client/components/ui/dropdown-menu';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
} from '@/client/components/ui/pagination';
import {
  RefreshCw,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
  CheckCircle,
  Copy,
  Check,
  ExternalLink,
} from 'lucide-react';

const DEFAULT_RANGE: WebhookLogRangeLabel = '24h';

type WebhookLogsSearch = {
  clientID?: string;
  range?: WebhookLogRangeLabel;
  status?: WebhookLogStatus;
  eventID?: string;
  page?: number;
};

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: 'monitoring/webhook-logs',
  validateSearch: (search: Record<string, unknown>): WebhookLogsSearch => {
    return {
      clientID: typeof search.clientID === 'string' ? search.clientID : undefined,
      range: typeof search.range === 'string' ? (search.range as WebhookLogRangeLabel) : undefined,
      status: typeof search.status === 'string' ? (search.status as WebhookLogStatus) : undefined,
      eventID: typeof search.eventID === 'string' ? search.eventID : undefined,
      page: typeof search.page === 'number' ? search.page : undefined,
    };
  },
  beforeLoad: async ({ context }) => {
    if (!context.auth.isAuthenticated) {
      throw redirect({ to: '/' });
    }
  },
  component: WebhookLogsComponent,
});

const STATUS_CONFIG: Record<
  WebhookLogStatus,
  { label: string; icon: typeof CheckCircle; className: string }
> = {
  ok: { label: 'OK', icon: CheckCircle, className: 'text-green-500' },
  error: { label: 'Error', icon: AlertTriangle, className: 'text-destructive' },
};

function StatusIcon({ status }: { status: WebhookLogStatus }) {
  const config = STATUS_CONFIG[status];
  const Icon = config.icon;
  return (
    <span title={config.label}>
      <Icon className={`h-4 w-4 ${config.className}`} />
    </span>
  );
}

/** Human-readable payload size */
function formatPayloadSize(chars: number): string {
  if (chars === 0) return '0';
  if (chars < 1024) return `${chars} B`;
  return `${(chars / 1024).toFixed(1)} KB`;
}

/** Copy-to-clipboard button that doesn't render the value it copies */
function CopyButton({
  value,
  label = 'Copy',
  disabled = false,
  className,
}: {
  value: string | null;
  label?: string;
  disabled?: boolean;
  className?: string;
}) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!value) return;
      navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    },
    [value],
  );

  return (
    <Button
      variant="outline"
      size="sm"
      onClick={handleCopy}
      disabled={disabled || !value}
      className={className}
      title={label}
    >
      {copied ? (
        <Check className="h-3.5 w-3.5 mr-1.5 text-green-500" />
      ) : (
        <Copy className="h-3.5 w-3.5 mr-1.5" />
      )}
      {copied ? 'Copied' : label}
    </Button>
  );
}

/** Truncated single-line payload preview — full value lives in the detail dialog */
function PayloadPreviewCell({ preview, length }: { preview: string | null; length: number }) {
  if (!preview || length === 0) {
    return <span className="text-xs text-muted-foreground">-</span>;
  }
  return (
    <div className="flex flex-col gap-0.5 min-w-0">
      <span className="font-mono text-xs truncate block max-w-[280px] text-muted-foreground">
        {preview.slice(0, 120)}
      </span>
      <span className="text-[10px] text-muted-foreground/70">{formatPayloadSize(length)}</span>
    </div>
  );
}

const columnHelper = createColumnHelper<WebhookLogRow>();

const columns = [
  columnHelper.accessor('status', {
    header: '',
    cell: (info) => <StatusIcon status={info.getValue()} />,
    size: 40,
    enableSorting: false,
  }),
  columnHelper.accessor('timeStamp', {
    header: 'Timestamp',
    cell: (info) => {
      const val = info.getValue();
      return (
        <span className="text-xs whitespace-nowrap">
          {val ? format(new Date(val), 'MMM d, yyyy HH:mm:ss') : '-'}
        </span>
      );
    },
    size: 170,
  }),
  columnHelper.accessor('clientName', {
    header: 'Client',
    cell: (info) => {
      const row = info.row.original;
      const name = info.getValue();
      if (!name) {
        return <span className="font-mono text-xs text-muted-foreground">{row.clientID}</span>;
      }
      return <ClientNameCell clientID={row.clientID} clientName={name} />;
    },
    size: 170,
  }),
  columnHelper.accessor('eventID', {
    header: 'Event ID',
    cell: (info) => {
      const val = info.getValue();
      return val ? (
        <span className="font-mono text-xs">{val}</span>
      ) : (
        <span className="text-xs text-muted-foreground">-</span>
      );
    },
    size: 100,
  }),
  columnHelper.accessor('callback', {
    header: 'Callback',
    cell: (info) => {
      const val = info.getValue();
      if (!val) return <span className="text-xs text-muted-foreground">-</span>;
      return (
        <span className="font-mono text-xs truncate block max-w-[240px]" title={val}>
          {val}
        </span>
      );
    },
    size: 240,
  }),
  columnHelper.display({
    id: 'result',
    header: 'Result',
    size: 220,
    cell: (info) => {
      const row = info.row.original;
      if (row.status === 'error') {
        return (
          <div className="flex items-center gap-2">
            {row.errorCode !== null && (
              <Badge variant="outline" className="border-destructive/40 text-destructive">
                {row.errorCode}
              </Badge>
            )}
            <span className="text-xs text-destructive truncate max-w-[160px]" title={row.errorMessage ?? ''}>
              {row.errorMessage ?? 'Error'}
            </span>
          </div>
        );
      }
      return <PayloadPreviewCell preview={row.returnPreview} length={row.returnLength} />;
    },
  }),
  columnHelper.display({
    id: 'inputValue',
    header: 'Payload',
    size: 300,
    cell: (info) => (
      <PayloadPreviewCell
        preview={info.row.original.inputPreview}
        length={info.row.original.inputLength}
      />
    ),
  }),
];

// ============================================================================
// Detail Dialog
// ============================================================================

function PayloadBlock({
  title,
  value,
  length,
}: {
  title: string;
  value: string | null;
  length: number;
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h4 className="text-sm font-medium">{title}</h4>
          <span className="text-xs text-muted-foreground">{formatPayloadSize(length)}</span>
        </div>
        <CopyButton value={value} label="Copy" />
      </div>
      <div className="rounded-md border bg-muted/30 p-3 max-h-[280px] overflow-auto">
        {value ? (
          <JsonSyntaxHighlight json={value} className="text-xs font-mono leading-relaxed" />
        ) : (
          <span className="text-xs text-muted-foreground">Empty</span>
        )}
      </div>
    </div>
  );
}

function WebhookLogDetailDialog({
  webhookIdx,
  onClose,
}: {
  webhookIdx: number | null;
  onClose: () => void;
}) {
  const { getAccessTokenSilently, logout } = useAuth0();
  const [detail, setDetail] = useState<WebhookLogDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (webhookIdx === null) {
      setDetail(null);
      setError(null);
      return;
    }

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = await getAccessTokenSilently();
        const response = await fetch(
          `${API_BASE_URL}/api/monitoring/webhook-logs/${webhookIdx}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );

        if (response.status === 401) {
          logout({ logoutParams: { returnTo: window.location.origin } });
          return;
        }
        if (!response.ok) throw new Error(`API error: ${response.status}`);

        const result: WebhookLogDetailResponse = await response.json();
        if (!cancelled) setDetail(result.log);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load webhook log');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [webhookIdx, getAccessTokenSilently, logout]);

  return (
    <Dialog open={webhookIdx !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {detail && <StatusIcon status={detail.status} />}
            Webhook Log #{webhookIdx}
          </DialogTitle>
          <DialogDescription>
            {detail?.timeStamp
              ? format(new Date(detail.timeStamp), 'MMM d, yyyy HH:mm:ss')
              : 'Full request and response payloads'}
          </DialogDescription>
        </DialogHeader>

        {loading && <div className="py-8 text-center text-muted-foreground">Loading...</div>}

        {error && (
          <div className="rounded-lg border border-destructive p-4 text-destructive text-sm">
            {error}
          </div>
        )}

        {detail && !loading && (
          <div className="space-y-4">
            {/* Metadata */}
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <div className="text-xs text-muted-foreground">Client</div>
                <div>
                  {detail.clientName ?? '—'}{' '}
                  <span className="font-mono text-xs text-muted-foreground">{detail.clientID}</span>
                </div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Event ID</div>
                <div className="font-mono text-xs">{detail.eventID ?? '—'}</div>
              </div>
              <div className="col-span-2">
                <div className="text-xs text-muted-foreground">Callback</div>
                <div className="flex items-center gap-2">
                  <span className="font-mono text-xs break-all">{detail.callback ?? '—'}</span>
                  {detail.callback && (
                    <a
                      href={detail.callback}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-muted-foreground hover:text-foreground flex-shrink-0"
                      title="Open callback URL"
                    >
                      <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  )}
                </div>
              </div>
              {detail.status === 'error' && (
                <div className="col-span-2">
                  <div className="text-xs text-muted-foreground">Error</div>
                  <div className="text-sm text-destructive">
                    {detail.errorCode !== null && (
                      <Badge variant="outline" className="mr-2 border-destructive/40 text-destructive">
                        {detail.errorCode}
                      </Badge>
                    )}
                    {detail.errorMessage}
                  </div>
                </div>
              )}
            </div>

            <PayloadBlock
              title="Request Payload (inputValue)"
              value={detail.inputValue}
              length={detail.inputLength}
            />
            <PayloadBlock
              title="Response (returnValue)"
              value={detail.returnValue}
              length={detail.returnLength}
            />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// Route Component
// ============================================================================

function WebhookLogsComponent() {
  const { getAccessTokenSilently, logout } = useAuth0();
  const navigate = useNavigate();
  const {
    clientID,
    range = DEFAULT_RANGE,
    status: statusFilter,
    eventID,
    page = 1,
  } = useSearch({ from: '/monitoring/webhook-logs' });

  usePersistedSearch('/monitoring/webhook-logs', {
    clientID: undefined,
    range: DEFAULT_RANGE,
    status: undefined,
    eventID: undefined,
    page: undefined,
  });

  const [data, setData] = useState<WebhookLogsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [detailIdx, setDetailIdx] = useState<number | null>(null);
  const [eventIDInput, setEventIDInput] = useState(eventID ?? '');

  const hours = useMemo(
    () => WEBHOOK_LOG_RANGES.find((r) => r.label === range)?.hours ?? WEBHOOK_LOG_RANGES[0].hours,
    [range],
  );

  const updateSearch = useCallback(
    (updates: Partial<WebhookLogsSearch>) => {
      navigate({
        to: '/monitoring/webhook-logs',
        search: {
          clientID: 'clientID' in updates ? updates.clientID : clientID,
          range: 'range' in updates ? updates.range : range,
          status: 'status' in updates ? updates.status : statusFilter,
          eventID: 'eventID' in updates ? updates.eventID : eventID,
          // Reset page when filters change (unless page is being set explicitly)
          page: 'page' in updates ? updates.page : 1,
        },
      });
    },
    [navigate, clientID, range, statusFilter, eventID],
  );

  // Keep the local input in sync when the search param changes elsewhere (reset, back nav)
  useEffect(() => {
    setEventIDInput(eventID ?? '');
  }, [eventID]);

  // Debounce eventID typing into the URL
  useEffect(() => {
    const trimmed = eventIDInput.trim();
    if (trimmed === (eventID ?? '')) return;

    const timer = setTimeout(() => {
      updateSearch({ eventID: trimmed || undefined });
    }, 500);
    return () => clearTimeout(timer);
  }, [eventIDInput, eventID, updateSearch]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const token = await getAccessTokenSilently();

      const params = new URLSearchParams();
      if (clientID) params.set('clientID', clientID);
      if (statusFilter) params.set('status', statusFilter);
      if (eventID) params.set('eventID', eventID);
      params.set('hours', String(hours));
      params.set('page', String(page));
      params.set('limit', String(WEBHOOK_LOGS_CONFIG.DEFAULT_LIMIT));

      const response = await fetch(`${API_BASE_URL}/api/monitoring/webhook-logs?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.status === 401) {
        logout({ logoutParams: { returnTo: window.location.origin } });
        return;
      }

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      const result: WebhookLogsResponse = await response.json();
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load webhook logs');
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently, logout, clientID, statusFilter, eventID, hours, page]);

  const forceRefresh = useCallback(async () => {
    const token = await getAccessTokenSilently();
    await fetch(`${API_BASE_URL}/api/cache/invalidate`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ pattern: 'webhook-logs:*' }),
    });
    loadData();
  }, [getAccessTokenSilently, loadData]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const table = useReactTable({
    data: data?.logs ?? [],
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const summary = data?.summary;

  return (
    <Layout>
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Webhook Logs</h1>
            <p className="text-sm text-muted-foreground">
              Read-only view of webhook delivery attempts and failures
            </p>
          </div>
          <div className="flex items-center gap-2">
            <CacheTimestamp cachedAt={data?.cacheInfo?.cachedAt ?? null} />
            <Button
              variant="ghost"
              size="icon"
              title="Force refresh"
              onClick={() => forceRefresh()}
              disabled={loading}
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>

        {/* Summary cards (double as quick filters) */}
        {summary && (
          <div className="grid grid-cols-3 gap-3">
            <button
              onClick={() => updateSearch({ status: undefined })}
              className={cn(
                'rounded-lg border p-3 text-left transition-colors hover:bg-accent',
                !statusFilter && 'ring-2 ring-ring',
              )}
            >
              <div className="text-sm text-muted-foreground">Total</div>
              <div className="text-2xl font-bold">{summary.total}</div>
            </button>
            <button
              onClick={() => updateSearch({ status: statusFilter === 'ok' ? undefined : 'ok' })}
              className={cn(
                'rounded-lg border p-3 text-left transition-colors hover:bg-accent',
                statusFilter === 'ok' && 'ring-2 ring-ring',
              )}
            >
              <div className="text-sm text-muted-foreground">OK</div>
              <div className="text-2xl font-bold text-green-600 dark:text-green-500">
                {summary.ok}
              </div>
            </button>
            <button
              onClick={() => updateSearch({ status: statusFilter === 'error' ? undefined : 'error' })}
              className={cn(
                'rounded-lg border p-3 text-left transition-colors hover:bg-accent',
                statusFilter === 'error' && 'ring-2 ring-ring',
                summary.error > 0 && 'border-destructive',
              )}
            >
              <div className="text-sm text-muted-foreground">Error</div>
              <div className={cn('text-2xl font-bold', summary.error > 0 && 'text-destructive')}>
                {summary.error}
              </div>
            </button>
          </div>
        )}

        {/* Filters */}
        <div className="flex items-center gap-2 flex-wrap">
          {/* Range filter */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                {range}
                <ChevronDown className="ml-2 h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              {WEBHOOK_LOG_RANGES.map((r) => (
                <DropdownMenuItem key={r.label} onClick={() => updateSearch({ range: r.label })}>
                  {r.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Status filter */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                {statusFilter ? STATUS_CONFIG[statusFilter].label : 'All Statuses'}
                <ChevronDown className="ml-2 h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem onClick={() => updateSearch({ status: undefined })}>
                All Statuses
              </DropdownMenuItem>
              {(Object.keys(STATUS_CONFIG) as WebhookLogStatus[]).map((s) => (
                <DropdownMenuItem key={s} onClick={() => updateSearch({ status: s })}>
                  {STATUS_CONFIG[s].label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Client selector, event ID search, reset, refresh - right justified */}
          <div className="ml-auto flex items-center gap-2">
            <ClientSelector
              value={clientID ?? ''}
              onChange={(id) => updateSearch({ clientID: id || undefined })}
              label=""
              className="min-w-[250px]"
            />
            <Input
              value={eventIDInput}
              onChange={(e) => setEventIDInput(e.target.value)}
              placeholder="Event ID"
              className="h-9 w-[150px]"
            />
            <ResetFiltersButton
              routePath="/monitoring/webhook-logs"
              defaultValues={{
                clientID: undefined,
                range: DEFAULT_RANGE,
                status: undefined,
                eventID: undefined,
              }}
            />
            <Button variant="outline" size="sm" onClick={loadData} disabled={loading}>
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Error state */}
        {error && (
          <div className="rounded-lg border border-destructive p-4 text-destructive">{error}</div>
        )}

        {/* Table */}
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <TableHead
                      key={header.id}
                      className="cursor-pointer select-none"
                      onClick={header.column.getToggleSortingHandler()}
                      style={{ width: header.getSize() }}
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      {header.column.getIsSorted() === 'asc'
                        ? ' ↑'
                        : header.column.getIsSorted() === 'desc'
                          ? ' ↓'
                          : ''}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {loading && !data ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="text-center py-8 text-muted-foreground"
                  >
                    Loading...
                  </TableCell>
                </TableRow>
              ) : table.getRowModel().rows.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="text-center py-8 text-muted-foreground"
                  >
                    No webhook logs found for the selected filters
                  </TableCell>
                </TableRow>
              ) : (
                table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    className="cursor-pointer hover:bg-muted"
                    onClick={() => setDetailIdx(row.original.webhookIdx)}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id} style={{ width: cell.column.getSize() }}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        {data?.pagination && data.pagination.totalPages > 1 && (
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              Page {data.pagination.page} of {data.pagination.totalPages} (
              {data.pagination.totalCount} total)
            </p>
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page <= 1}
                    onClick={() => updateSearch({ page: page - 1 })}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                </PaginationItem>
                <PaginationItem>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page >= data.pagination.totalPages}
                    onClick={() => updateSearch({ page: page + 1 })}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
        )}
      </div>

      <WebhookLogDetailDialog webhookIdx={detailIdx} onClose={() => setDetailIdx(null)} />
    </Layout>
  );
}
